import { CircleAlert as CircleAlertIcon, PencilIcon } from 'components/icons';
import { FormattedDate, useFormatDate } from 'components/misc/FormattedDate';
import { EmployeeNameLink } from 'components/record-chip/RecordLink';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import HoursDisplay from 'components/util/HoursDisplay';
import { timecardSecondsToHours } from 'pages/timecards/utils/timecardUtils';
import { Ref } from 'react';
import { FormattedMessage } from 'react-intl';
import { formatAmount } from 'components/util/MoneyDisplay';
import { getClassifierText, t } from 'utils/getTranslations';
import { useEmployeeNameFormatter } from 'utils/nameOrder';
import { InvoiceDetailsParameterizedTracker } from '../InvoiceDetailsChangeTrackers';
import { ITEM_EDIT_CELL, ITEM_FIGURE, ITEM_SELECT_CELL, ITEM_SUB_FIGURE } from '../InvoiceItemTable';
import { itemTableRowClassName } from '../InvoiceItemRow';
import { AdvancedBillingDisplay } from './component/AdvancedBillingDisplay';
import { BilledHoursDisplay } from './component/BilledHoursDisplay';
import { DescriptionDisplay } from './component/DescriptionDisplay';
import { TakeActionBackDisplay } from './component/TakeActionBackDisplay';
import { getTimecardIssueMessages } from './component/TimecardIssueIndicator';
import { TotalAmountDisplay } from './component/TotalAmountDisplay';
import { InvoiceTimecardRowProps, useInvoiceTimecardRow } from './InvoiceTimecardTableItem';

/**
 * A timecard as a row of the new design's Timecards table: its content is the classic row's, its cells the table's
 * columns. In a narrow card the figures fold under the entry and their cells go.
 */
export function InvoiceTimecardTableRow({
    measureRef,
    virtualIndex,
    isNarrow,
    ...props
}: InvoiceTimecardRowProps & { measureRef: Ref<HTMLTableRowElement>; virtualIndex: number; isNarrow: boolean }) {
    const {
        invoiceTimecard,
        timecardForm,
        index,
        readonly,
        control,
        setValue,
        getValues,
        handleUpdateInvoiceTimecards,
        classifiers,
        advancedBillingEnabled,
        isCredit,
        isNew,
        multiplierTypes,
        buttonsDisabled,
        isTwoDecimalDisplayEnabled,
        isTwoDecimalEntryEnabled,
        displayDecimalScale,
        isSelected,
        toggleSelection,
    } = props;
    const {
        isPaymentCurrencyMode,
        displayCurrency,
        invoiceTimecardEmployee,
        invoiceTimecardDate,
        invoiceTimecardHourlyPrice,
        invoiceTimecardActivityTypeCode,
        editIntentTypeCode,
        isNonBillable,
        isRowDisabled,
        canTakeActionBack,
        hideSelectionCheckbox,
        rowState,
        nonBillableBgStyle,
        submitWithCatch,
        submitDescription,
        openInvoiceTimecardEditModal,
    } = useInvoiceTimecardRow(props);
    const formatName = useEmployeeNameFormatter();
    const formatDate = useFormatDate();
    const { timecard, employee } = invoiceTimecard;
    const activityTypeCode = invoiceTimecardActivityTypeCode || timecard.timecardActivityTypeCode;
    const issues = getTimecardIssueMessages(invoiceTimecard.issues);
    // What names this row in its controls' labels: its date and its lawyer.
    const rowName = { date: formatDate(invoiceTimecardDate) ?? '', name: employee ? formatName(employee) : '' };

    const billedHours = (
        <BilledHoursDisplay
            invoiceTimecard={invoiceTimecard}
            readonly={readonly}
            isRowDisabled={isRowDisabled}
            advancedBillingEnabled={advancedBillingEnabled}
            control={control}
            index={index}
            onSubmit={submitWithCatch}
            setValue={setValue}
            buttonsDisabled={buttonsDisabled}
            isTwoDecimalDisplayEnabled={isTwoDecimalDisplayEnabled}
            isTwoDecimalEntryEnabled={isTwoDecimalEntryEnabled}
            displayDecimalScale={displayDecimalScale}
            editLabel={t('invoices.details.table.edit-billed-hours', rowName)}
        />
    );
    // Mid-sentence in a narrow row's meta line it reads lower case.
    const usedHours = (
        <FormattedMessage
            id={isNarrow ? 'invoices.details.table.used-hours-inline' : 'invoices.details.table.used-hours'}
            values={{
                hours: (
                    <HoursDisplay
                        value={timecardSecondsToHours(
                            timecardForm?.totalTimeInSeconds ?? 0,
                            isTwoDecimalDisplayEnabled
                        )}
                    />
                ),
            }}
        />
    );
    // A timecard with no rate is billed at zero, so the missing rate is said in the row's warning colour.
    const hourlyRate =
        invoiceTimecardHourlyPrice == null ? (
            <span className="text-warning-ink font-medium">{t('invoices.details.table.no-hourly-rate')}</span>
        ) : (
            `${formatAmount(Number(invoiceTimecardHourlyPrice))} ${displayCurrency}/h`
        );
    const amount = (
        <TotalAmountDisplay
            invoiceTimecard={invoiceTimecard}
            currency={displayCurrency}
            decimalScale={displayDecimalScale}
            isPaymentCurrencyMode={isPaymentCurrencyMode}
        />
    );
    const advancedBilling = (
        <AdvancedBillingDisplay
            readonly={readonly}
            isRowDisabled={isRowDisabled}
            control={control}
            index={index}
            onSubmit={submitWithCatch}
            setValue={setValue}
            getValues={getValues}
            multiplierTypes={multiplierTypes}
            invoiceTimecard={invoiceTimecard}
            handleUpdateInvoiceTimecards={handleUpdateInvoiceTimecards}
            buttonsDisabled={buttonsDisabled}
            isPaymentCurrencyMode={isPaymentCurrencyMode}
        />
    );

    return (
        <tr
            ref={measureRef}
            data-index={virtualIndex}
            className={itemTableRowClassName(rowState)}
            style={nonBillableBgStyle}
        >
            <td className={ITEM_SELECT_CELL}>
                {hideSelectionCheckbox ? null : (
                    <Checkbox
                        className="mt-0.5"
                        aria-label={t('invoices.details.table.select-timecard', rowName)}
                        disabled={buttonsDisabled}
                        checked={isSelected}
                        onCheckedChange={() => toggleSelection(invoiceTimecard.id)}
                    />
                )}
            </td>
            <td className="px-2 py-3">
                {isRowDisabled && canTakeActionBack ? (
                    <div className="mb-2">
                        <TakeActionBackDisplay
                            index={index}
                            invoiceTimecard={invoiceTimecard}
                            editIntentTypeCode={editIntentTypeCode}
                            setValue={setValue}
                            getValues={getValues}
                            control={control}
                            handleUpdateInvoiceTimecards={handleUpdateInvoiceTimecards}
                        />
                    </div>
                ) : null}
                <div className="opacity-decreasing-when-disabled">
                    <div className="text-meta flex flex-wrap items-center gap-x-2.5 gap-y-1">
                        {issues.length ? <CircleAlertIcon aria-hidden className="text-warning-ink size-4" /> : null}
                        {employee ? <EmployeeNameLink entity={employee} to={`/employee/${employee.id}`} /> : null}
                        <InvoiceDetailsParameterizedTracker
                            currentParam={invoiceTimecardEmployee?.id}
                            displayValue={timecard?.employee ? formatName(timecard.employee) : undefined}
                            storedParam={timecard?.employee?.id}
                            textWrap="nowrap"
                        />
                        <span className="text-foreground-secondary tabular-nums">
                            <FormattedDate value={invoiceTimecardDate} fallback="" />
                        </span>
                        {activityTypeCode ? (
                            <span className="text-foreground-tertiary">
                                {getClassifierText(classifiers['TIMECARD_ACTIVITY_TYPE'], activityTypeCode)}
                            </span>
                        ) : null}
                        {isNonBillable ? (
                            <Badge className="bg-neutral-tint text-foreground-secondary">
                                <FormattedMessage id="timecards.form.is-non-billable" />
                            </Badge>
                        ) : null}
                        {isNew ? (
                            <Badge className="bg-card text-success-ink font-semibold">
                                <FormattedMessage id="invoices.details.table.new-row" />
                            </Badge>
                        ) : null}
                    </div>
                    <DescriptionDisplay
                        invoiceTimecard={invoiceTimecard}
                        readonly={readonly}
                        isRowDisabled={isRowDisabled}
                        control={control}
                        index={index}
                        onSubmit={submitDescription}
                        buttonsDisabled={buttonsDisabled}
                        editLabel={t('invoices.details.table.edit-description', rowName)}
                    />
                </div>
                {issues.map((issue) => (
                    <p key={issue} className="text-caption text-warning-ink m-0 mt-1.5">
                        <span className="sr-only">{t('invoices.details.table.issue-prefix')} </span>
                        {t(issue)}
                    </p>
                ))}
                {isNarrow ? (
                    <div className="opacity-decreasing-when-disabled mt-1.5 flex flex-col gap-2">
                        <div className="text-meta flex items-baseline justify-between gap-3">
                            {/* One plain run: the billed figure keeps its column weight only in the wide row. */}
                            <span className="text-foreground-tertiary flex flex-wrap items-baseline gap-x-1.5 tabular-nums [&_.font-semibold]:font-normal">
                                {billedHours}
                                <span aria-hidden="true">·</span>
                                {usedHours}
                                <span aria-hidden="true">·</span>
                                {hourlyRate}
                            </span>
                            <span className="text-body1 font-semibold">{amount}</span>
                        </div>
                        {advancedBillingEnabled ? advancedBilling : null}
                    </div>
                ) : null}
            </td>
            {isNarrow ? null : (
                <td className="w-40 px-2 py-3 text-end">
                    <div className="opacity-decreasing-when-disabled flex flex-col items-end">
                        <div className={ITEM_FIGURE}>{billedHours}</div>
                        <div className={ITEM_SUB_FIGURE}>{usedHours}</div>
                        <div className={ITEM_SUB_FIGURE}>{hourlyRate}</div>
                    </div>
                </td>
            )}
            {!isNarrow && advancedBillingEnabled ? <td className="w-60 px-2 py-3">{advancedBilling}</td> : null}
            {isNarrow ? null : (
                <td className="w-32 px-2 py-3 text-end">
                    <div className="text-body1 flex justify-end font-semibold">{amount}</div>
                </td>
            )}
            <td className={ITEM_EDIT_CELL}>
                {isCredit || readonly || isRowDisabled ? null : (
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('invoices.details.table.edit-timecard', rowName)}
                        disabled={buttonsDisabled}
                        onClick={openInvoiceTimecardEditModal}
                        // The row's pencil stays light: a 15px glyph at the board's 1.5 stroke.
                        className="text-foreground-tertiary new:[&_svg[stroke]]:stroke-[1.5]"
                    >
                        <PencilIcon className="size-[15px]" />
                    </Button>
                )}
            </td>
        </tr>
    );
}
